import React, { createContext, useContext, useState, useEffect } from 'react';
import contentData from '../data/content.json';
import apiClient from '../services/apiClient';

const ContentContext = createContext();

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(contentData);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isOnlineMode, setIsOnlineMode] = useState(false);
  const [needsInitialUpload, setNeedsInitialUpload] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('offline');

  // Check if SQLite backend is available (no auth required for health check)
  const checkBackendConnection = async () => {
    try {
      // Health check doesn't require authentication
      const response = await fetch(`${apiClient.baseURL}/health`);
      if (response.ok) {
        setConnectionStatus('online');
        if (apiClient.getStoredToken() && !apiClient.isTokenExpired()) {
          setIsOnlineMode(true);
        }
        return true;
      }
      throw new Error('Health check failed');
    } catch (error) {
      console.error('SQLite backend not available:', error.message);
      setConnectionStatus('offline');
      setIsOnlineMode(false);
      return false;
    }
  };

  // Load content from SQLite backend and update it with new structure if needed
  const loadContent = async (forceBackend = false) => {
    setIsLoading(true);
    try {
      // Try to load from backend first (no auth needed for reading content)
      console.log('📡 Loading content from SQLite backend...');
      
      try {
        // Try authenticated first, then fall back to public
        const hasValidToken = apiClient.getStoredToken() && !apiClient.isTokenExpired();
        const response = hasValidToken 
          ? await apiClient.getAllContent()
          : await apiClient.getPublicContent();
        if (response.success && response.content && Object.keys(response.content).length > 0) {
          const backendContent = response.content;
          
          // COMPREHENSIVE MERGE: Backend overrides, Local fills missing
          console.log('🔄 Merging backend content with local content...');
          
          // Helper function to deep merge objects
          const deepMerge = (local, backend) => {
            const result = { ...local };
            
            for (const key in backend) {
              if (backend[key] && typeof backend[key] === 'object' && !Array.isArray(backend[key])) {
                // Recursively merge nested objects
                result[key] = deepMerge(result[key] || {}, backend[key]);
              } else {
                // Override with backend value (arrays and primitives)
                result[key] = backend[key];
              }
            }
            
            return result;
          };
          
          // Start with local content as base, let backend override
          const mergedContent = deepMerge(contentData, backendContent);
          
          
          console.log('📋 Content merge completed - backend data takes precedence, local fills gaps');
          setContent(mergedContent);
          console.log('✅ Content loaded from SQLite backend with comprehensive merge');
          
          setConnectionStatus('online');
          // Only set online mode if authenticated
          const hasValidToken = apiClient.getStoredToken() && !apiClient.isTokenExpired();
          setIsOnlineMode(hasValidToken);
        } else {
          throw new Error('Backend has no content');
        }
      } catch (backendError) {
        console.log('📄 Backend not available, using default content');
        setContent(contentData);
        setConnectionStatus('offline');
        setIsOnlineMode(false);
      }
    } catch (error) {
      console.error('❌ Failed to connect to SQLite backend:', error);
      console.log('📄 Using default content as fallback');
      setContent(contentData);
      setConnectionStatus('offline');
      setIsOnlineMode(false);
    } finally {
      setIsLoading(false);
    }
  };


  // Update a specific content section in SQLite backend
  const updateContent = async (path, value) => {
    setContent(prevContent => {
      const newContent = { ...prevContent };
      const pathArray = path.split('.');
      let current = newContent;
      
      // Navigate to the parent of the target property
      for (let i = 0; i < pathArray.length - 1; i++) {
        if (!current[pathArray[i]]) {
          current[pathArray[i]] = {};
        }
        current = current[pathArray[i]];
      }
      
      // Set the target property
      current[pathArray[pathArray.length - 1]] = value;
      
      return newContent;
    });
    setHasUnsavedChanges(true);

    // Auto-update SQLite backend (non-blocking - fails silently if backend unavailable)
    try {
      await apiClient.updateContentAtPath(path, value);
      console.log(`✅ Content updated in SQLite backend: ${path}`);
    } catch (error) {
      console.warn(`⚠️ Backend update failed for ${path}: ${error.message}`);
      console.log('💾 Content updated locally. Will sync when backend is available.');
      // Don't throw - content is already updated in state
    }
  };

  // Get content at a specific path
  const getContent = (path) => {
    const pathArray = path.split('.');
    let current = content;
    
    for (const key of pathArray) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }
    
    return current;
  };

  // Save content to SQLite backend ONLY
  const saveContent = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 300)); // Brief delay for UX
      
      console.log('💾 Saving content to SQLite backend...');
      const response = await apiClient.saveAllContent(content);
      
      if (response.success) {
        setHasUnsavedChanges(false);
        console.log('✅ Content saved to SQLite backend successfully');
        return { 
          success: true, 
          message: response.message || 'Contenu sauvegardé avec succès dans la base SQLite!'
        };
      } else {
        throw new Error(response.message || 'Backend save failed');
      }
    } catch (error) {
      console.error('❌ Failed to save to SQLite backend:', error);
      return {
        success: false,
        message: `❌ Erreur: Le backend est inaccessible. Veuillez vérifier que le backend est démarré et que la configuration CORS est correcte. Erreur: ${error.message}`
      };
    } finally {
      setIsLoading(false);
    }
  };





  // Initialize content on mount
  useEffect(() => {
    loadContent();
  }, []);

  // Periodic connection check
  useEffect(() => {
    const interval = setInterval(checkBackendConnection, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Trigger content reload when authenticated
  const reloadContentAfterAuth = async () => {
    console.log('🔄 Reloading content after authentication...');
    await loadContent(true); // Force backend reload
  };

  const value = {
    content,
    updateContent,
    getContent,
    saveContent,
    isLoading,
    hasUnsavedChanges,
    isOnlineMode,
    needsInitialUpload,
    setNeedsInitialUpload,
    connectionStatus,
    checkBackendConnection,
    loadContent,
    reloadContentAfterAuth
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};